import React, { useCallback, useState } from "react";

import {
  ActionButton,
  BottomActionSheet,
  CancelButton,
  HeaderInfo,
  HeaderSubtitle,
  HeaderTitle,
  SheetCard,
  SheetHeader,
} from "../../../../../components/BottomActionSheet";
import { DateFormat, formatCurrency, formatDate } from "../../../../../utils";
import { ContributionItemProps } from "./types";
import { KebabButton } from "../../../../../components/KebabButton";
import { CardRow, CardTopRow, CardDate, CardChange, CardMeta } from "./styles";

export const ContributionCard: React.FC<ContributionItemProps> = ({
  item,
  actions,
}) => {
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const isInflow = item.direction === "in";
  const changeText = formatCurrency(item.amount);
  const dateText = formatDate(item.date, DateFormat.DMY_SPACE);
  const metaText = `Invested ${formatCurrency(item.investedAtPoint)} · Value ${formatCurrency(
    item.portfolioValueAtPoint,
  )}`;

  const visibleActions = actions.filter((action) => action.show);
  const hasActions = visibleActions.length > 0;

  const openSheet = useCallback(() => {
    setIsSheetOpen(true);
  }, []);

  const closeSheet = useCallback(() => {
    setIsSheetOpen(false);
  }, []);

  const runAction = useCallback(
    (action: () => void) => {
      closeSheet();
      action();
    },
    [closeSheet],
  );

  return (
    <CardRow>
      <CardTopRow>
        <CardDate>{dateText}</CardDate>
        {hasActions && (
          <KebabButton
            size="small"
            onClick={openSheet}
            aria-label="Contribution options"
            aria-haspopup="dialog"
            data-testid={`contribution-actions-mobile-${item.id}`}
          />
        )}
      </CardTopRow>
      <CardChange $positive={isInflow}>{changeText}</CardChange>
      <CardMeta>{metaText}</CardMeta>

      {hasActions && (
        <BottomActionSheet
          isOpen={isSheetOpen}
          onClose={closeSheet}
          ariaLabel="Contribution actions"
        >
          <SheetCard>
            <SheetHeader>
              <HeaderInfo>
                <HeaderTitle>{changeText}</HeaderTitle>
                <HeaderSubtitle>{dateText}</HeaderSubtitle>
              </HeaderInfo>
            </SheetHeader>
            {visibleActions.map((action) => (
              <ActionButton
                key={action.id}
                type="button"
                $variant={
                  action.variant === "destructive" ? "destructive" : undefined
                }
                onClick={() => runAction(action.onClick)}
              >
                {action.label}
              </ActionButton>
            ))}
          </SheetCard>
          <SheetCard>
            <CancelButton type="button" onClick={closeSheet}>
              Cancel
            </CancelButton>
          </SheetCard>
        </BottomActionSheet>
      )}
    </CardRow>
  );
};
